import React from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Stack,
  Divider,
  TextInput,
  Button,
  Group,
  Select,
} from '@mantine/core'
import WaterfallChartComponent from '../../stories/WaterfallChart/WaterfallChart'
import { DataPoint, useMetrics } from '../api'
import { useWidgetContext } from '../providers'
import { WidgetWrapper } from '../WidgetWrapper'
import { z } from 'zod'
import { useWidgetEditor } from '../state'
import { WaterfallChartOptions } from '../schemas'
import classes from './common.module.css'

type OptionsType = z.infer<typeof WaterfallChartOptions>

export default function WaterfallChart(props: { id: string }) {
  const ctx = useWidgetContext()
  const editor = useWidgetEditor()
  const config = editor.useConfig(props.id)
  const { data: metrics } = useMetrics()

  if (config.widget !== 'WaterfallChart')
    throw new Error('Invalid widget type, expected WaterfallChart')

  const options: OptionsType = config.options

  // Generate months from competition start to end
  const months = React.useMemo(() => {
    if (!ctx.competition_start || !ctx.competition_end) {
      // Fallback to year-based generation if competition dates not available
      const contextPeriod = ctx.period || '2025-01-01'
      const year = dayjs(contextPeriod).year()
      return Array.from({ length: 12 }, (_, i) => {
        const date = dayjs(`${year}-01-01`).add(i, 'month')
        return {
          period: date.startOf('month').format('YYYY-MM-DD'),
          label: date.format('MMM'),
        }
      })
    }

    const startDate = dayjs(ctx.competition_start).startOf('month')
    const endDate = dayjs(ctx.competition_end).endOf('month')
    const monthsArray = []

    let currentDate = startDate
    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, 'month')
    ) {
      monthsArray.push({
        period: currentDate.format('YYYY-MM-DD'),
        label: currentDate.format('MMM'),
      })
      currentDate = currentDate.add(1, 'month')
    }

    return monthsArray
  }, [ctx.competition_start, ctx.competition_end, ctx.period])

  const metricKey = options.metric || ctx.metric || ''
  const metric = metrics.find((m) => m.key === metricKey)
  const isPercentMetric = metric?.is_percent_metric || false
  const title = options.title || metric?.name || 'Monthly Performance'

  return (
    <WidgetWrapper
      id={props.id}
      transformData={(data: DataPoint[]) => {
        const periodValues = new Map<string, number>()
        data.forEach((point) => {
          if (point.value !== null) {
            periodValues.set(point.period, point.value)
          }
        })

        const chartData = months.map((month) => {
          const value = periodValues.get(month.period)

          if (value === undefined) {
            return {
              label: month.label,
              value: null,
            }
          }

          if (isPercentMetric) {
            return {
              label: month.label,
              value: value,
            }
          }

          return {
            label: month.label,
            value: value - 1,
          }
        })

        return { chartData, title }
      }}
      render={({ chartData, title }) => (
        <Box className={classes.chart}>
          <WaterfallChartComponent
            data={chartData}
            valueLabel={title}
            normalBarChart={isPercentMetric}
          />
        </Box>
      )}
      query={() => ({
        period_type: 'month',
        period_range: false,
        periods: months.map((m) => m.period),
        subject_type: ctx.subject_type || 'user',
        subject_ids: [ctx.subject || 0],
        metrics: [metricKey || ''],
        components: [isPercentMetric ? 'actual' : 'percent'], // Always use percent component
        statistics: ['value'], // Always use value statistic
        competition_id: ctx.competition_id,
      })}
    />
  )
}

// Form component for WaterfallChart options
export function WaterfallChartForm({ id }: { id: string }) {
  const editor = useWidgetEditor()
  const isSaving = editor.useIsSaving()
  const config = editor.useConfig(id)
  const [formValues, setFormValues] = React.useState(config.options)
  const { data: metrics } = useMetrics()

  if (config.widget !== 'WaterfallChart')
    throw new Error('Invalid widget type, expected WaterfallChart')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    editor.setOptions(formValues)
    editor.save()
  }

  // Prepare metrics for select with empty option
  const metricOptions = [
    { value: '', label: '' },
    ...metrics.map((metric) => ({
      value: metric.key,
      label: metric.name,
    })),
  ]

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="sm">
        <Divider label="Waterfall Chart Options" />
        <TextInput
          label="Title"
          value={formValues.title || ''}
          onChange={(e) =>
            setFormValues({ ...formValues, title: e.currentTarget.value })
          }
          placeholder="Monthly Performance"
        />
        <Select
          label="Metric"
          value={formValues.metric || ''}
          onChange={(value) =>
            setFormValues({ ...formValues, metric: value || '' })
          }
          data={metricOptions}
          placeholder="Select a metric"
          searchable
          clearable
        />
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button color="red.4" variant="outline" onClick={editor.endEditWidget}>
          Cancel
        </Button>
        <Button type="submit" loading={isSaving}>
          Save
        </Button>
      </Group>
    </form>
  )
}

export const WaterfallChartRegistry = {
  component: WaterfallChart,
  formComponent: WaterfallChartForm,
  mock: () => (
    <Box
      p="md"
      style={{ backgroundColor: 'white', borderRadius: '8px', height: '200px' }}
    >
      <WaterfallChartComponent
        data={[
          { label: 'Jan', value: 0.15 },
          { label: 'Feb', value: 0.05 },
          { label: 'Mar', value: -0.03 },
          { label: 'Apr', value: 0.08 },
          { label: 'May', value: -0.02 },
        ]}
        valueLabel="Monthly Performance"
      />
    </Box>
  ),
  getInitialOptions: () => WaterfallChartOptions.parse({}),
}
